import React from 'react';
import { Course, Plan } from '../../types';
import { ModernCard } from '../ui/ModernCard.tsx';
import { ModernButton } from '../ui/ModernButton.tsx';
import { CheckIcon } from '../icons/index.tsx';

interface SuccessStepProps {
  selectedCourse: Course | null;
  selectedPlan: Plan | null;
  selectedDeliverables: string[];
  paymentBreakdown: any;
  calculatedPrice: number;
  taskData?: {
    id?: number;
    public_id?: string;
    title?: string;
    due_date?: string;
    status?: string;
    payment_breakdown?: {
      total_price: number;
      credit_used: number;
      balance_used: number;
      external_payment: number;
      payment_method?: string;
    };
  };
  onCreateAnother: () => void;
}

export const SuccessStep: React.FC<SuccessStepProps> = ({
  selectedCourse,
  selectedPlan,
  selectedDeliverables,
  paymentBreakdown,
  calculatedPrice,
  taskData,
  onCreateAnother
}) => {
  // Get payment details from URL if coming from callback
  const urlParams = new URLSearchParams(window.location.search);
  const creditFromUrl = parseFloat(urlParams.get('credit_used') || '0');
  const externalFromUrl = parseFloat(urlParams.get('external_paid') || '0');
  const totalFromUrl = parseFloat(urlParams.get('total_price') || '0');

  // Use payment breakdown from taskData if available, otherwise use URL params
  const paymentInfo = taskData?.payment_breakdown || {
    total_price: totalFromUrl || paymentBreakdown?.total_price || calculatedPrice,
    credit_used: creditFromUrl,
    external_payment: externalFromUrl,
    balance_used: 0
  };

  return (
    <div className="space-y-6 text-center">
      <ModernCard className="p-8">
        <div className="mb-6">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckIcon />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">تم إنشاء المهمة بنجاح!</h2>
          <p className="text-gray-600">تم إنشاء مهمتك ومعالجة الدفع.</p>
        </div>

        <div className="space-y-4">
          {/* Task ID Display */}
          {taskData?.public_id && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h3 className="font-semibold text-blue-900 mb-2">ID المهمة</h3>
              <p className="text-lg font-mono text-blue-800 bg-white px-3 py-2 rounded-lg border">
                {taskData.public_id}
              </p>
              <p className="text-xs text-blue-600 mt-2">
                احتفظ بهذا المعرف للمراجعة المستقبلية
              </p>
            </div>
          )}

          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-semibold text-gray-900 mb-2">تفاصيل المهمة</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">المادة:</span>
                <span className="font-medium">{selectedCourse?.display_name || 'المادة المحددة'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">الخطة:</span>
                <span className="font-medium">{selectedPlan?.title || 'الخطة المحددة'}</span>
              </div>
              {selectedDeliverables.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">المخرجات:</span>
                  <span className="font-medium">{selectedDeliverables.join('، ')}</span>
                </div>
              )}
              
              {/* Payment Details Section */}
              <hr className="my-2" />
              <div className="flex justify-between">
                <span className="text-gray-600">
                  {paymentInfo.external_payment === 0 
                    ? 'المبلغ على الإعانة:' 
                    : paymentInfo.credit_used > 0
                      ? 'تفاصيل الدفع:'
                      : 'المبلغ المدفوع:'}
                </span>
                <div className="text-right">
                  {paymentInfo.credit_used > 0 && paymentInfo.external_payment > 0 ? (
                    <>
                      <div className="font-medium text-blue-600">
                        على الإعانة: {paymentInfo.credit_used} KWD
                      </div>
                      <div className="font-medium text-green-600">
                        دفع نقدي: {paymentInfo.external_payment} KWD
                      </div>
                      <div className="font-medium text-gray-800 text-xs mt-1">
                        المجموع: {paymentInfo.total_price} KWD
                      </div>
                    </>
                  ) : paymentInfo.external_payment === 0 ? (
                    <span className="font-medium text-blue-600">
                      {paymentInfo.credit_used || paymentInfo.total_price} KWD
                    </span>
                  ) : (
                    <span className="font-medium text-green-600">
                      {paymentInfo.external_payment || paymentInfo.total_price} KWD
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="text-sm text-gray-600">
            <p>يمكنك متابعة تقدم مهمتك من لوحة التحكم</p>
          </div>
        </div>
      </ModernCard>

      <div className="flex gap-4">
        <ModernButton 
          variant="outline" 
          onClick={onCreateAnother}
          className="flex-1"
        >
          إنشاء مهمة أخرى
        </ModernButton>
      </div>
    </div>
  );
};